import React, { useState, useMemo, useCallback, useEffect } from "react";
import type { DiseaseTaxonomyNode } from "../../types/diseaseTaxonomy.types";
import DiseaseTreeBrowser from "../DiseaseBrowser/DiseaseTreeBrowser";
import DiseaseAutocomplete from "../DiseaseBrowser/DiseaseAutocomplete";
import PopularDiseasesList from "../DiseaseBrowser/PopularDiseasesList";
import {
  getDiseaseDisplayName,
  getDiseaseTooltipWithMondo,
} from "../../utils/diseaseDisplayName";

interface GeneTherapeuticHeaderProps {
  selectedGene?: string;
  therapeuticArea?: string;
  taxonomyNodes?: DiseaseTaxonomyNode[];
  selectedDiseaseIds?: Set<number>;
  onDiseaseSelectionChange?: (ids: Set<number>) => void;
  showDiseaseControls?: boolean;
  onSubmitDiseases?: () => void;
  /** Temporary flag: when true, user can only select one disease at a time across all controls */
  allowSingleSelection?: boolean;
}

const TA_DISPLAY: Record<string, string> = {
  cardiovascular: "Cardiovascular",
  renal: "Renal",
};


const OntologyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="2.5" />
    <circle cx="5" cy="19" r="2.5" />
    <circle cx="19" cy="19" r="2.5" />
    <line x1="12" y1="7.5" x2="5" y2="16.5" />
    <line x1="12" y1="7.5" x2="19" y2="16.5" />
  </svg>
);

export const GeneTherapeuticHeader: React.FC<GeneTherapeuticHeaderProps> = ({
  selectedGene = "",
  therapeuticArea = "",
  taxonomyNodes,
  selectedDiseaseIds,
  onDiseaseSelectionChange,
  showDiseaseControls = false,
  onSubmitDiseases,
  allowSingleSelection = true,
}) => {
  const [treeBrowserOpen, setTreeBrowserOpen] = useState(false);

  useEffect(() => {
    setTreeBrowserOpen(false);
  }, [therapeuticArea]);

  const nodeMapById = useMemo(() => {
    if (!taxonomyNodes) return new Map<number, DiseaseTaxonomyNode>();
    const m = new Map<number, DiseaseTaxonomyNode>();
    for (const n of taxonomyNodes) m.set(n.id, n);
    return m;
  }, [taxonomyNodes]);

  // Display only explicitly selected nodes (parent selections represent their subtrees)
  const selectedDiseaseNames = useMemo(() => {
    if (!selectedDiseaseIds || selectedDiseaseIds.size === 0) return [];
    const names: { id: number; name: string; mondo_id: string }[] = [];
    for (const id of selectedDiseaseIds) {
      const n = nodeMapById.get(id);
      if (n) names.push({ id: n.id, name: n.name, mondo_id: n.mondo_id });
    }
    return names;
  }, [selectedDiseaseIds, nodeMapById]);

  // Console.log the selection
  useEffect(() => {
    if (selectedDiseaseNames.length > 0) {
      const commaSeparated = selectedDiseaseNames
        .map((d) => getDiseaseDisplayName(d.name))
        .join(", ");
      console.log("Diseases Selected:", commaSeparated);
    }
  }, [selectedDiseaseNames]);

  const handleSelectionChange = useCallback(
    (ids: Set<number>) => {
      onDiseaseSelectionChange?.(ids);
    },
    [onDiseaseSelectionChange]
  );

  const removeDisease = useCallback(
    (id: number) => {
      if (!selectedDiseaseIds) return;
      const next = new Set(selectedDiseaseIds);
      next.delete(id);
      onDiseaseSelectionChange?.(next);
    },
    [selectedDiseaseIds, onDiseaseSelectionChange]
  );

  const taLabel = TA_DISPLAY[therapeuticArea] ?? therapeuticArea;
  const hasTaxonomy = showDiseaseControls && taxonomyNodes && taxonomyNodes.length > 0;

  return (
    <>
      {/* Row 1 — Gene | Therapeutic Area */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-900">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2 shrink-0">
            <span className="h-5 w-5 flex items-center justify-center text-red-500 dark:text-red-400 text-xl" role="img" aria-label="DNA">
              🧬
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">Gene:</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{selectedGene}</span>
            </div>
          </div>

          {therapeuticArea && (
            <>
              <div className="h-5 w-px bg-gray-300 dark:bg-gray-700 shrink-0" />
              <div className="flex items-center gap-2 shrink-0">
                <span className="h-5 w-5 flex items-center justify-center text-xl" role="img" aria-label={taLabel}>
                  🏥
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Therapeutic Area:</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{taLabel}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Row 2 — Popular Disease Ontology + Disease Ontology controls */}
      {hasTaxonomy && (
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-900 mt-2 overflow-visible">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
            {/* Popular Diseases — quick access to commonly used diseases */}
            <PopularDiseasesList
              nodes={taxonomyNodes!}
              selectedIds={selectedDiseaseIds ?? new Set()}
              onSelectionChange={handleSelectionChange}
              therapeuticArea={therapeuticArea}
              allowSingleSelection={allowSingleSelection}
            />

            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700 shrink-0" />

            <button
              onClick={() => setTreeBrowserOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-slate-600 dark:hover:border-blue-500 transition-all duration-200 shrink-0"
              title="Browse full disease ontology"
            >
              <OntologyIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              Disease Ontology
            </button>

            <div className="w-72 min-w-[10rem] max-w-72 shrink">
              <DiseaseAutocomplete
                nodes={taxonomyNodes!}
                selectedIds={selectedDiseaseIds ?? new Set()}
                onSelectionChange={handleSelectionChange}
                allowSingleSelection={allowSingleSelection}
              />
            </div>
            
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
              <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1 min-w-0 max-w-md">
                <span className="text-sm text-gray-700 dark:text-gray-300 shrink-0 whitespace-nowrap">
                  Disease Selected:
                </span>
                {selectedDiseaseNames.length === 0 ? (
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    All
                  </span>
                ) : (
                  <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1.5 min-w-0">
                    {selectedDiseaseNames.slice(0, 30).map((d) => {
                      const displayName = getDiseaseDisplayName(d.name);
                      return (
                        <span
                          key={d.id}
                          title={getDiseaseTooltipWithMondo(d.name, d.mondo_id)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full border border-indigo-200 dark:border-indigo-700/50 cursor-default w-max max-w-[min(100%,14rem)]"
                        >
                          <span className="min-w-0 truncate">{displayName}</span>
                          <button
                            type="button"
                            onClick={() => removeDisease(d.id)}
                            className="hover:text-red-600 dark:hover:text-red-400 transition-colors shrink-0"
                            aria-label={`Remove ${displayName}`}
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </span>
                      );
                    })}
                    {selectedDiseaseNames.length > 30 && (
                      <span className="text-xs text-slate-400">
                        +{selectedDiseaseNames.length - 30} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={onSubmitDiseases}
                disabled={selectedDiseaseNames.length === 0}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg shrink-0 transition-all duration-200 ${
                  selectedDiseaseNames.length === 0
                    ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm hover:shadow"
                }`}
                title={selectedDiseaseNames.length === 0 ? "Select diseases to submit" : "Submit selected diseases"}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tree Browser Popup */}
      {treeBrowserOpen && hasTaxonomy && (
        <DiseaseTreeBrowser
          nodes={taxonomyNodes!}
          selectedIds={selectedDiseaseIds ?? new Set()}
          onSelectionChange={handleSelectionChange}
          therapeuticArea={therapeuticArea}
          onClose={() => setTreeBrowserOpen(false)}
          allowSingleSelection={allowSingleSelection}
        />
      )}
    </>
  );
};
